/**
 * For `<script data-ece-quiz-auto>`: `data-article-url`, then page `?articleUrl=`, then
 * `VITE_DEV_ARTICLE_URL` (baked at build). Returns `undefined` so `resolveArticleUrl` can use the
 * current page URL when all are omitted.
 */
export function resolveArticleUrlOverrideFromAutoScript(script: HTMLScriptElement): string | undefined {
  const fromAttr = script.getAttribute('data-article-url')?.trim();
  if (fromAttr) return fromAttr;
  if (typeof window !== 'undefined') {
    const fromQuery = new URLSearchParams(window.location.search).get('articleUrl')?.trim();
    if (fromQuery) return fromQuery;
  }
  const fromEnv = (import.meta.env.VITE_DEV_ARTICLE_URL ?? '').trim();
  if (fromEnv) return fromEnv;
  return undefined;
}

/**
 * Pathname suffixes removed when matching Umbraco `articleURL` (cosmetic extensions on the live site).
 * Longest first so `.html` is handled before `.htm`.
 */
export const ARTICLE_PATH_STRIP_EXTENSIONS: readonly string[] = ['.html', '.aspx', '.htm'];

function stripAllowlistedPathExtension(pathname: string): string {
  const lower = pathname.toLowerCase();
  for (const ext of ARTICLE_PATH_STRIP_EXTENSIONS) {
    if (lower.endsWith(ext)) {
      return pathname.slice(0, -ext.length);
    }
  }
  return pathname;
}

/**
 * Strips at most one allowlisted extension from the URL pathname and clears the hash.
 * Used so Delivery `articleUrl:` matches CMS when the browser shows e.g. `.aspx` and Umbraco does not.
 */
export function normalizeArticleUrlForDelivery(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    url.pathname = stripAllowlistedPathExtension(url.pathname);
    url.search = '';
    url.hash = '';
    return url.href;
  } catch {
    return trimmed;
  }
}

/**
 * Canonical article URL for Delivery API `articleUrl:` filter.
 * Override when the browser URL does not match what Umbraco stores (e.g. client-only routes).
 */
export function resolveArticleUrl(override?: string): string {
  let raw: string;
  if (override?.trim()) {
    raw = override.trim();
  } else if (typeof window === 'undefined') {
    return '';
  } else {
    raw = window.location.href.split('#')[0] ?? window.location.href;
  }
  return normalizeArticleUrlForDelivery(raw);
}

/** Stable `sessionStorage` key per canonical article URL (`eceq:v1:<hash>`). */
export function sessionStorageKeyForArticle(articleUrl: string): string {
  return `eceq:v1:${hashArticleUrl(articleUrl)}`;
}

/** Small deterministic hash (djb2-style) so keys stay short and URL-safe. */
function hashArticleUrl(url: string): string {
  let h = 5381;
  for (let i = 0; i < url.length; i++) {
    h = Math.imul(h, 33) ^ url.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}
